/**
 * useFileUpload — multi-file upload helper backed by Vault Documents.
 *
 * Preserves the legacy hook surface (`uploadFiles`, `uploads`,
 * `cancelUpload`, `UploadProgress`, `onUploadComplete`, `onUploadError`,
 * `defaultCategory`) so chat / contact-book / dropped-AI-context callers
 * keep working without rewrites. Internally drives `api.uploadFileToVault`.
 *
 * The per-file result the consumer sees mirrors the historical
 * `UserFileResponse`-ish shape (`fileId`, `fileName`, `mimeType`,
 * `sizeBytes`) since the legacy callers reach for those field names.
 */

import { useCallback, useRef, useState } from 'react';
import { api, type VaultDocumentDto, type VaultInputPurposeDto } from '@/lib/api';

export interface UploadedFileResult {
  fileId: string;
  fileName: string;
  mimeType: string | null;
  sizeBytes: number;
}

export interface UploadProgress {
  fileName: string;
  /** Status names match the legacy hook for backwards compatibility:
   *   - `pending`    — queued, no work yet
   *   - `processing` / `uploading` — actively uploading (synonyms)
   *   - `done`       — finished; `fileId` is set
   *   - `failed`     — terminal error; `error` is set
   *   - `cancelled`  — user cancelled before completion
   */
  status: 'pending' | 'processing' | 'uploading' | 'done' | 'failed' | 'cancelled';
  progress: number;
  /** Set once `status === 'done'`. */
  fileId?: string;
  error?: string;
}

export interface UseFileUploadOptions {
  onUploadComplete?: (file: UploadedFileResult) => void;
  onUploadError?: (fileName: string, error: Error) => void;
  /** Legacy category hint — folded into tags for Vault. Optional. */
  defaultCategory?: string;
  /** What the files are for, when they are an arche's input (a contact's
   * attachment): Fias files them into that purpose's My-Fias folder instead
   * of the user's Documents. */
  purpose?: VaultInputPurposeDto;
}

export interface UseFileUploadResult {
  uploadFiles: (
    files: File[] | FileList,
    options?: { tags?: string[]; folderId?: string; category?: string },
  ) => Promise<UploadedFileResult[]>;
  uploads: UploadProgress[];
  cancelUpload: (fileName: string) => void;
  isUploading: boolean;
}

function vaultDocToLegacyShape(doc: VaultDocumentDto): UploadedFileResult {
  return {
    fileId: doc.documentId,
    fileName: doc.name,
    mimeType: doc.mimeType,
    sizeBytes: doc.sizeBytes,
  };
}

export function useFileUpload(opts: UseFileUploadOptions = {}): UseFileUploadResult {
  const [isUploading, setIsUploading] = useState(false);
  const [uploads, setUploads] = useState<UploadProgress[]>([]);
  const cancelledRef = useRef<Set<string>>(new Set());
  const { onUploadComplete, onUploadError, defaultCategory, purpose } = opts;

  const updateUpload = useCallback((fileName: string, patch: Partial<UploadProgress>) => {
    setUploads((prev) => prev.map((u) => (u.fileName === fileName ? { ...u, ...patch } : u)));
  }, []);

  const cancelUpload = useCallback(
    (fileName: string) => {
      cancelledRef.current.add(fileName);
      updateUpload(fileName, { status: 'cancelled' });
    },
    [updateUpload],
  );

  const uploadFiles = useCallback(
    async (
      files: File[] | FileList,
      options?: { tags?: string[]; folderId?: string; category?: string },
    ): Promise<UploadedFileResult[]> => {
      const arr = Array.from(files);
      if (arr.length === 0) return [];
      setIsUploading(true);
      setUploads((prev) => [
        ...prev,
        ...arr.map<UploadProgress>((f) => ({
          fileName: f.name,
          status: 'pending',
          progress: 0,
        })),
      ]);
      try {
        const memberships = await api.listMyVaultMemberships();
        if (!memberships.personalHolder) {
          throw new Error('Personal workspace not provisioned');
        }
        const holderId = memberships.personalHolder.holderId;
        const tags = [
          ...(options?.tags ?? []),
          ...(options?.category ? [options.category] : []),
          ...(defaultCategory ? [defaultCategory] : []),
        ];
        const results: UploadedFileResult[] = [];
        for (const file of arr) {
          if (cancelledRef.current.has(file.name)) continue;
          updateUpload(file.name, { status: 'uploading', progress: 0 });
          try {
            const uploadOptions: {
              tags?: string[];
              folderId?: string;
              purpose?: VaultInputPurposeDto;
            } = {};
            if (tags.length > 0) uploadOptions.tags = tags;
            if (purpose) uploadOptions.purpose = purpose;
            else if (options?.folderId) uploadOptions.folderId = options.folderId;
            const doc = await api.uploadFileToVault(
              file,
              holderId,
              Object.keys(uploadOptions).length > 0 ? uploadOptions : undefined,
            );
            const result = vaultDocToLegacyShape(doc);
            results.push(result);
            updateUpload(file.name, {
              status: 'done',
              progress: 1,
              fileId: doc.documentId,
            });
            onUploadComplete?.(result);
          } catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            updateUpload(file.name, { status: 'failed', error: error.message });
            onUploadError?.(file.name, error);
          }
        }
        return results;
      } finally {
        setIsUploading(false);
      }
    },
    [defaultCategory, purpose, onUploadComplete, onUploadError, updateUpload],
  );

  return { uploadFiles, uploads, cancelUpload, isUploading };
}
